import { Map as LeafletMap, latLngBounds } from 'leaflet';
import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Esri from 'esri-leaflet';
import get from 'lodash.get';

import {
  esriDemographicsServer,
  endpoints,
  esriAreaLayers,
} from '../../../config';
import { humanizeNumber } from '../../../utils/format';

export class AreasLayerComponent extends Component {

  static contextTypes = Component.contextTypes;
  static childContextTypes = Component.childContextTypes;
  static contextTypes = {
    map: PropTypes.instanceOf(LeafletMap),
  };

  constructor(props) {
    super(props);

    this.state = {
      features: [],
    };

    this.setLayer = this.setLayer.bind(this);
  }

  componentDidMount() {
    const env = process.env.REACT_APP_API || 'local';

    const { demographicsUrl, demographicsLayersEndPoint } = endpoints[env];

    const proxy = demographicsUrl + demographicsLayersEndPoint;

    const defaultFeatureConfig = {
      url: `${esriDemographicsServer}/25`,
      proxy,
      fields: ['OBJECTID', 'ID', 'NAME', 'TOTPOP_CY'],
      style: {
        weight: 2,
        fillOpacity: 0,
        opacity: 1,
        color: this.props.activeColor || '#FF0000',
      },
    };


    const features = esriAreaLayers.map(config => {
      return {
        layer: config.name,
        feature: Esri.featureLayer({
          ...defaultFeatureConfig,
          ...config,
          url: `${esriDemographicsServer}/${config.layer}`,
          where: this.getAreaQueryString(config.name),
          onEachFeature: (...args) => {
            return this.setLayer({ name: config.name })(...args);
          },
        }),
      };
    });

    Promise.race(features.map(feature => {
      if (feature.layer === this.props.layer) {
        feature.feature.addTo(this.context.map);
        feature.feature.once('load', this.handleLoad(feature.feature));
      }

      if (this.props.useEDCPopup && feature.layer === 'states') {
        feature.feature.addTo(this.context.map, true);
      }

      return new Promise(resolve => {
        feature.feature.once('load', resolve);
      });
    }))
    .then(() => {
      this.setState({ features });
    });
  }

  componentDidUpdate(previous) {
    const features = this.state.features || [];

    if (previous.layer !== this.props.layer) {
      features.forEach(feature => {
        if (feature.layer !== this.props.layer) {
          feature.feature.removeFrom(this.context.map);
        } else if (feature.layer === this.props.layer) {
          feature.feature.addTo(this.context.map);
          feature.feature.once('load', this.handleLoad(feature.feature));
        }

        if (this.props.useEDCPopup && feature.layer === 'states') {
          feature.feature.addTo(this.context.map, true);
        }
      });
    }

    if (previous.saveSearchedItems !== this.props.saveSearchedItems) {
      this.context.map.setZoom(5);
      this.tempResult = null;
      features.forEach(feature => {
        if (feature.layer === this.props.layer) {
          feature.feature.eachFeature(subFeat => {
            subFeat.remove();
          });
          feature.feature.setWhere(this.getAreaQueryString(feature.layer));
          feature.feature.once('load', this.handleLoad(feature.feature));
        }
      });
    }
  }

  componentWillUnmount() {
    const features = this.state.features || [];
    features.forEach(feature => {
      if (feature && feature.feature && feature.feature.remove && feature.feature._map) {
        feature.feature.remove();
      }
    });
  }

  getAreas(layer) {
    if (this.tempResultLayer === layer && this.tempResult) {
      return this.tempResult;
    }

    let result = this.props.areas[layer] || [];
    if ((layer === 'communities' || layer === 'counties') && this.props.saveSearchedItems.length) {
      const temp = result.filter(area => {
        return this.props.saveSearchedItems.includes(area.fips_code);
      });
      result = temp.length ? temp : result;
      this.tempResult = result;
      this.tempResultLayer = layer;
    }
    return result;
  }

  getRegionsQueryString() {
    const countyIds = this.getAreas('regions').reduce((ids, region) => {
      (region.counties || []).forEach(c => {
        if (c && ids.indexOf(c.county_id) === -1) {
          ids.push(c.county_id);
        }
      });
      return ids;
    }, []);

    const counties = this.props.areas.counties;

    const idStrings = counties
    .filter(c => countyIds.indexOf(c.county_id) !== -1)
    .reduce((codes, area) => {
      if (area.fips_code && !Number.isNaN(area.fips_code) && codes.indexOf(area.fips_code) === -1) {
        codes.push(area.fips_code);
      }

      return codes;
    }, [])
    .map(a => `'${a}'`)
    .join(',');

    return `"ID" IN (${idStrings})`;
  }

  getAreaQueryString(layer) {
    if (layer === 'regions') {
      return this.getRegionsQueryString();
    }

    const areas = this.getAreas(layer);
    const idStrings = areas
    .reduce((codes, area) => {
      if (area.fips_code && !Number.isNaN(area.fips_code) && codes.indexOf(area.fips_code) === -1) {
        codes.push(area.fips_code);
      }

      return codes;
    }, [])
    .map(a => `'${a}'`)
    .join(',');

    return `"ID" IN (${idStrings})`;
  }

  setLayer(layerValue) {
    return (...args) => {
      // for the onEachFeature method the layer is the second argument
      // for the eachFeature method, the layer is the first argumen
      const layer = args[1] || args[0];
      const id = get(layer, 'feature.properties.ID');
      const areas = this.getAreas(layerValue.name)

      .filter(a => {
        if (layerValue.name === 'regions') {
          return a.counties.map(rc => rc && rc.fips_code).find(rfc => rfc === id);
        }
        return a.fips_code === id;
      })
      .sort((a, b) => {
        return get(a, 'basicInfo.about.name', '')
          .toLowerCase()
          .localeCompare(
            get(b, 'basicInfo.about.name', '')
            .toLowerCase(),
          );
      });

      if (this.props.useEDCPopup && layerValue.name === 'states') {
        // do not bind popup to state
      } else if (this.props.useEDCPopup && layerValue.name !== 'states') {
        this.bindEDCPopup(layer, areas);
      } else {
        this.bindTooltip(layer, areas, layerValue);
      }
    };
  }

  handleLoad(feature) {
    return () => {
      const bounds = latLngBounds([]);
      let boundsUpdated = false;
      if (feature) {
        // feature.eachActiveFeature(feat => {
        feature.eachFeature(feat => {
          if (feat._map) {
            bounds.extend(feat.getBounds());
            this.setLayer({ name: this.props.layer })(feat);
            boundsUpdated = true;
          }
        });
        if (boundsUpdated) {
          this.context.map.fitBounds(bounds, { padding: [25, 25] });
        }
      }
    };
  }

  bindEDCPopup(layer, areas) {
    // eslint-disable-next-line no-confusing-arrow
    const ensureProtocol = url => url.includes('//') ? url : `http://${url}`;
    const removeProtocol = url => url.replace(/^https?:\/\//i, '');

    const contactHtml = areas.map(area => {
      let name = get(area, 'basicInfo.about.name');
      if (get(area, 'basicInfo.about.state')) {
        name += `, ${get(area, 'basicInfo.about.state')}`;
      }

      let primary = get(area, 'contacts.economicDevelopmentContacts', []).find(c => c.isPrimary);

      if (!primary) {
        primary = get(area, 'contacts.economicDevelopmentContacts[0]', {});
      }

      const contact = [
        'name',
        'email',
        'organization',
        'webAddr',
        'phone',
        'fax',
        'street',
        'city',
        'state',
        'zip',
      ].reduce((str, field) => {
        const value = get(primary, field);
        if (field === 'webAddr' && value) {
          return `${str}<br /><a href="${ensureProtocol(value)}" target="_blank">${removeProtocol(value)}</a>`;
        } else if (value) {
          return `${str}<br />${value}`;
        }

        return str;
      }, '');

      return `<b>${name}</b><br />${contact}`;
    })
    .filter(a => a)
    .join('<br /><br />');


    layer.bindPopup(
      `<div>

        ${contactHtml}
      </div>`,
      { sticky: true },
    );
  }

  bindTooltip(layer, areas, layerValue) {
    const names = areas.map(a => {
      let name = get(a, 'basicInfo.about.name');
      if (get(a, 'basicInfo.about.state')) {
        name += `, ${get(a, 'basicInfo.about.state')}`;
      }
      return name;
    })
    .filter(a => a)
    .join('<br />');

    layer.bindTooltip(
      `<div>
        <b>${names}</b>
        <br />Population: ${humanizeNumber(layer.feature.properties.TOTPOP_CY)}
      </div>`,
      { sticky: true },
    );
    layer.on('click', () => {
      this.props.handleClick(layerValue.name, areas[0]);
    });
  }

  render() {
    return null;
  }
}

AreasLayerComponent.contextTypes = {
  map: PropTypes.instanceOf(LeafletMap),
};

AreasLayerComponent.propTypes = {
  useEDCPopup: PropTypes.bool,
  activeColor: PropTypes.string,
  layer: PropTypes.string,
  areas: PropTypes.object,
  handleClick: PropTypes.func,
  saveSearchedItems: PropTypes.arrayOf(PropTypes.string),
};

function mapStateToProps({ saveSearchedItems }) {
  return {
    saveSearchedItems,
  };
}

export default connect(mapStateToProps)(AreasLayerComponent);



// WEBPACK FOOTER //
// ./src/components/Map/Layers/Areas.js